import { useGT } from 'gt-react';
import { CircleNotchIcon, MicrophoneIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useVoiceInput, type UseVoiceInputOptions } from './useVoiceInput';

interface VoiceInputButtonProps extends UseVoiceInputOptions {
  className?: string;
}

/**
 * Hold-to-speak microphone button for chat composers. Self-contained: it owns
 * the voice-capture lifecycle via `useVoiceInput`, so any composer can drop it
 * in and get the exact same recording / transcribing affordance. Used by the
 * real `ChatInput` composer and by the demo copilots alike, so every chat
 * surface gets an identical mic.
 */
export function VoiceInputButton({
  className,
  ...opts
}: VoiceInputButtonProps) {
  const gt = useGT();
  const { recording, isTranscribing, pressHandlers } = useVoiceInput(opts);

  const label = recording
    ? gt('Release to send', {
        context: 'tooltip while holding the chat mic button',
      })
    : gt('Hold to speak', {
        context: 'tooltip for the chat voice input mic button',
      });

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      {...pressHandlers}
      disabled={opts.disabled}
      className={cn(
        'h-7 w-7 p-0 select-none',
        recording &&
          'bg-red-600 text-white hover:bg-red-600 hover:text-white animate-pulse',
        isTranscribing && !recording && 'opacity-70',
        className,
      )}
      title={label}
      aria-label={label}
    >
      {isTranscribing && !recording ? (
        <CircleNotchIcon className="h-4 w-4 animate-spin text-stone-500" />
      ) : (
        <MicrophoneIcon
          className="h-4 w-4"
          weight={recording ? 'fill' : 'regular'}
        />
      )}
    </Button>
  );
}
